import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import heroImage from "../assets/hero-bg.png";
import trustedCarImage from "../assets/trusted-car.png";

import {
  Shield,
  RotateCcw,
  Award,
  Home,
  DollarSign,
  CreditCard,
  Wrench,
  Sparkles,
  CheckCircle,
  Headphones,
  Users,
  MapPin,
  Car,
  TrendingUp,
  Heart,
  Clock,
  Rocket,
  Globe,
  Star
} from 'lucide-react';

/* ─────────────────────────── journey data ─────────────────────────── */
const journeyYears = ['2026', '2027'];

const journeyData = {
  '2026': {
    cards: [
      {
        id: 1,
        icon: '🚀',
        title: 'Caryugo Founded',
        desc: 'Born from a vision to make car buying transparent & hassle-free',
        color: '#FFD60A',
        textColor: '#003566',
        top: '8%',
        left: '4%',
      },
      {
        id: 2,
        icon: '🌐',
        title: 'Platform Goes Live',
        desc: 'Launched our digital marketplace for pre-owned cars',
        color: '#003566',
        textColor: '#FFD60A',
        top: '8%',
        right: '4%',
        left: 'auto',
      },
      {
        id: 3,
        icon: '📍',
        title: '5 Cities Coverage',
        desc: 'Indore, Mhow, Ujjain, Dewas & Pithampur',
        color: '#00B4D8',
        textColor: '#fff',
        bottom: '12%',
        left: '4%',
        top: 'auto',
      },
      {
        id: 4,
        icon: '🚗',
        title: '10+ Cars Listed',
        desc: 'Quality-checked vehicles ready for confident buyers',
        color: '#FF6B35',
        textColor: '#fff',
        bottom: '12%',
        right: '4%',
        left: 'auto',
        top: 'auto',
      },
    ],
  },
  '2027': {
    upcoming: true,
    cards: [
      {
        id: 5,
        icon: '🗺️',
        title: 'Pan-India Expansion',
        desc: 'Scaling to 20+ cities across India',
        color: '#FFD60A',
        textColor: '#003566',
        top: '8%',
        left: '4%',
      },
      {
        id: 6,
        icon: '📈',
        title: '10,000+ Cars',
        desc: 'Massive inventory & AI-powered matching',
        color: '#003566',
        textColor: '#FFD60A',
        top: '8%',
        right: '4%',
        left: 'auto',
      },
    ],
  },
};

/* ─────────────────────────── component ─────────────────────────── */
const AboutUs = () => {
  const navigate = useNavigate();
  const [selectedYear, setSelectedYear] = useState('2026');
  const [animating, setAnimating] = useState(false);
  const [counts, setCounts] = useState({ cars: 0, cities: 0, experts: 0 });

  /* animated counters */
  useEffect(() => {
    const targets = { cars: 10, cities: 5, experts: 10 };
    const steps = 60;
    const interval = 2000 / steps;
    const timer = setInterval(() => {
      setCounts(prev => ({
        cars:    prev.cars    < targets.cars    ? Math.min(prev.cars    + Math.ceil(targets.cars    / steps), targets.cars)    : targets.cars,
        cities:  prev.cities  < targets.cities  ? Math.min(prev.cities  + Math.ceil(targets.cities  / steps), targets.cities)  : targets.cities,
        experts: prev.experts < targets.experts ? Math.min(prev.experts + Math.ceil(targets.experts / steps), targets.experts) : targets.experts,
      }));
    }, interval);
    return () => clearInterval(timer);
  }, []);

  const handleYearChange = (year) => {
    if (year === selectedYear || animating) return;
    setAnimating(true);
    setTimeout(() => {
      setSelectedYear(year);
      setAnimating(false);
    }, 280);
  };

  const currentData = journeyData[selectedYear];

  const features = [
    { icon: Shield,      title: "210-Point Quality Check",    description: "Every car is tested for top performance and safety" },
    { icon: RotateCcw,   title: "7-Day Return Promise",       description: "Don't love it? Return it, no questions asked" },
    { icon: Award,       title: "Certified Pre-Owned Cars",   description: "Verified history and complete documentation" },
    { icon: Home,        title: "Home Test Drive & Delivery", description: "Experience comfort from your doorstep" },
    { icon: DollarSign,  title: "Transparent Pricing",        description: "No hidden charges, ever" },
    { icon: CreditCard,  title: "Easy EMI & Loan Options",    description: "Flexible plans that fit your budget" },
    { icon: Wrench,      title: "1-Year Free Service Warranty", description: "Drive worry-free with guaranteed care" },
    { icon: Sparkles,    title: "Smart Match Technology",     description: "Get AI-powered car recommendations" },
    { icon: CheckCircle, title: "Verified Buyers & Sellers",  description: "100% safe and secure transactions" },
    { icon: Headphones,  title: "24x7 Expert Support",        description: "We're here whenever you need us" },
  ];

  return (
    <div style={styles.container}>
      {/* ── Hero ── */}
      <section style={styles.hero}>
        <div style={styles.heroOverlay}>
          <div style={styles.heroContent}>
            <h1 style={styles.heroTitle}>Driving Dreams, Delivering Trust</h1>
            <p style={styles.heroSubtitle}>India's Most Transparent Way to Buy & Sell Pre-Owned Cars</p>
            <div style={styles.heroButtons}>
              <button style={styles.primaryBtn} onClick={() => navigate('/buy-cars')}>Browse Cars</button>
              <button style={styles.secondaryBtn} onClick={() => navigate('/sell-car')}>Sell Your Car</button>
            </div>
          </div>
        </div>
      </section>

      {/* ── Stats ── */}
      <section style={styles.statsSection}>
        <div style={styles.statsGrid}>
          <div style={styles.statCard}>
            <Car size={40} color="#FFD60A" />
            <h3 style={styles.statNumber}>{counts.cars}+</h3>
            <p style={styles.statLabel}>Quality Cars</p>
          </div>
          <div style={styles.statCard}>
            <MapPin size={40} color="#FFD60A" />
            <h3 style={styles.statNumber}>{counts.cities}+</h3>
            <p style={styles.statLabel}>Cities Covered</p>
          </div>
          <div style={styles.statCard}>
            <TrendingUp size={40} color="#FFD60A" />
            <h3 style={styles.statNumber}>{counts.experts}+</h3>
            <p style={styles.statLabel}>Expert Team</p>
          </div>
        </div>
      </section>

      {/* ── Our Story ── */}
      <section style={styles.storySection}>
        <div style={styles.sectionContent}>
          <div style={styles.storyText}>
            <h2 style={styles.sectionTitle}>Our Story</h2>
            <p style={styles.paragraph}>
              Caryugo is a web platform developed by <strong>Aucwini Technologies LLP</strong>, set to launch in <strong>2026</strong> with a vision to simplify and transform the car buying and selling experience.
            </p>
            <p style={styles.paragraph}>
              As a technology-driven company, we built this platform after identifying key challenges in the used car market — lack of trust, complicated processes, unclear pricing, and dependence on unorganized dealers.
            </p>
            <p style={styles.paragraph}>
              We are starting our journey with <strong>5 cities — Indore, Mhow, Ujjain, Dewas, and Pithampur</strong>, focusing on building a reliable and easy-to-use platform where users can confidently buy and sell cars.
            </p>
            <p style={styles.paragraph}>
              Our core focus is on <strong>building trust, providing fair and competitive pricing, and ensuring a smooth and hassle-free process</strong> for every user.
            </p>
            <p style={styles.paragraph}>
              As we grow, our vision is to expand across India and become a trusted digital platform for pre-owned car transactions.
            </p>
          </div>
          <div style={styles.storyImage}>
            <div style={styles.imagePlaceholder} />
          </div>
        </div>
      </section>

      {/* ── Mission & Vision ── */}
      <section style={styles.missionSection}>
        <div style={styles.missionGrid}>
          <div style={styles.missionCard}>
            <Heart size={50} color="#FFD60A" />
            <h3 style={styles.missionTitle}>Our Mission</h3>
            <p style={styles.missionText}>
              To make car ownership accessible, affordable, and hassle-free for every Indian family through technology and transparency.
            </p>
          </div>
          <div style={styles.missionCard}>
            <TrendingUp size={50} color="#FFD60A" />
            <h3 style={styles.missionTitle}>Our Vision</h3>
            <p style={styles.missionText}>
              To become India's most trusted pre-owned car platform, setting new standards for quality, service, and customer satisfaction.
            </p>
          </div>
        </div>
      </section>

      {/* ── Why Choose Us ── */}
      <section style={styles.featuresSection}>
        <h2 style={{ ...styles.sectionTitle, textAlign: 'center' }}>Why Choose Caryugo?</h2>
        <p style={styles.sectionSubtitle}>10 Reasons Why We're Different From The Rest</p>
        <div style={styles.featuresGrid}>
          {features.map((feature, index) => (
            <div key={index} style={styles.featureCard}>
              <div style={styles.featureIcon}><feature.icon size={32} color="#003566" /></div>
              <h3 style={styles.featureTitle}>{feature.title}</h3>
              <p style={styles.featureDesc}>{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ════════════════════════════════════════
           OUR JOURNEY — PhonePe-style timeline
          ════════════════════════════════════════ */}
      <section style={styles.journeySection}>
        {/* heading */}
        <h2 style={styles.journeyHeading}>Our Journey</h2>

        {/* stage: year number + floating cards */}
        <div style={styles.journeyStage}>

          {/* SVG road / path decoration */}
          <svg style={styles.roadSvg} viewBox="0 0 900 320" preserveAspectRatio="none">
            <path
              d="M -50 200 Q 200 80 450 160 Q 700 240 950 120"
              fill="none"
              stroke="rgba(0,53,102,0.08)"
              strokeWidth="60"
              strokeLinecap="round"
            />
            <path
              d="M -50 200 Q 200 80 450 160 Q 700 240 950 120"
              fill="none"
              stroke="rgba(255,214,10,0.15)"
              strokeWidth="6"
              strokeDasharray="18 14"
              strokeLinecap="round"
            />
          </svg>

          {/* scattered dot pattern */}
          {Array.from({ length: 28 }).map((_, i) => (
            <div
              key={i}
              style={{
                position: 'absolute',
                width: i % 3 === 0 ? 8 : 5,
                height: i % 3 === 0 ? 8 : 5,
                borderRadius: '50%',
                backgroundColor: 'rgba(0,53,102,0.12)',
                top: `${10 + (i * 31 + i * 7) % 75}%`,
                left: `${(i * 37 + 5) % 92}%`,
                pointerEvents: 'none',
              }}
            />
          ))}

          {/* floating milestone cards */}
          {currentData.cards.map((card) => (
            <div
              key={card.id}
              style={{
                ...styles.milestoneCard,
                backgroundColor: card.color,
                color: card.textColor,
                top:    card.top    ?? 'auto',
                bottom: card.bottom ?? 'auto',
                left:   card.left   ?? 'auto',
                right:  card.right  ?? 'auto',
                opacity: animating ? 0 : 1,
                transform: animating ? 'translateY(16px) scale(0.95)' : 'translateY(0) scale(1)',
                transition: 'opacity 0.28s ease, transform 0.28s ease',
              }}
            >
              {/* dashed connector line */}
              <div style={{
                ...styles.connector,
                borderColor: card.color,
                bottom: card.top !== 'auto' ? '-38px' : 'auto',
                top:    card.bottom !== 'auto' ? '-38px' : 'auto',
                left: '50%',
                transform: 'translateX(-50%)',
              }} />

              <span style={styles.cardEmoji}>{card.icon}</span>
              <h4 style={{ ...styles.cardTitle, color: card.textColor }}>{card.title}</h4>
              <p  style={{ ...styles.cardDesc,  color: card.textColor }}>{card.desc}</p>
            </div>
          ))}

          {/* big year number */}
          <div style={{
            ...styles.bigYear,
            opacity: animating ? 0 : 1,
            transform: animating ? 'scale(0.88)' : 'scale(1)',
            transition: 'opacity 0.28s ease, transform 0.28s ease',
          }}>
            {selectedYear}
            {currentData.upcoming && (
              <span style={styles.upcomingBadge}>Vision</span>
            )}
          </div>

          {/* isometric cube accents */}
          <svg style={styles.cubeLeft} viewBox="0 0 60 70" fill="none">
            <polygon points="30,0 60,17 60,52 30,70 0,52 0,17" fill="rgba(255,214,10,0.13)" stroke="rgba(255,214,10,0.35)" strokeWidth="1.5"/>
            <line x1="30" y1="0"  x2="30" y2="70" stroke="rgba(255,214,10,0.25)" strokeWidth="1"/>
            <line x1="0"  y1="17" x2="60" y2="52" stroke="rgba(255,214,10,0.25)" strokeWidth="1"/>
            <line x1="60" y1="17" x2="0"  y2="52" stroke="rgba(255,214,10,0.25)" strokeWidth="1"/>
          </svg>
          <svg style={styles.cubeRight} viewBox="0 0 44 52" fill="none">
            <polygon points="22,0 44,13 44,39 22,52 0,39 0,13" fill="rgba(0,53,102,0.10)" stroke="rgba(0,53,102,0.30)" strokeWidth="1.5"/>
            <line x1="22" y1="0"  x2="22" y2="52" stroke="rgba(0,53,102,0.20)" strokeWidth="1"/>
          </svg>
        </div>

        {/* ── horizontal year nav ── */}
        <div style={styles.yearNav}>
          <div style={styles.yearTrack}>
            {/* progress line */}
            <div style={styles.yearTrackLine} />

            {journeyYears.map((year, idx) => (
              <div
                key={year}
                style={styles.yearStop}
                onClick={() => handleYearChange(year)}
              >
                <div style={{
                  ...styles.yearDot,
                  backgroundColor: selectedYear === year ? '#003566' : '#ccc',
                  transform: selectedYear === year ? 'scale(1.35)' : 'scale(1)',
                  boxShadow: selectedYear === year ? '0 0 0 5px rgba(0,53,102,0.18)' : 'none',
                  transition: 'all 0.3s ease',
                  cursor: year === '2027' ? 'default' : 'pointer',
                  opacity: year === '2027' ? 0.55 : 1,
                }} />
                <span style={{
                  ...styles.yearLabel,
                  color: selectedYear === year ? '#003566' : '#999',
                  fontWeight: selectedYear === year ? '700' : '400',
                  transition: 'color 0.3s ease',
                }}>
                  {year}
                  {year === '2027' && <span style={styles.visionTag}> ✦</span>}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* ═══════════════════════════════════════════════════════════ */}

      {/* ── How It Works ── */}
      <section style={styles.howItWorksSection}>
        <h2 style={{ ...styles.sectionTitle, textAlign: 'center' }}>How It Works</h2>
        <div style={styles.stepsGrid}>
          {[
            { n: 1, t: 'Browse & Select',   d: 'Choose from 10+ quality-checked cars' },
            { n: 2, t: 'Book Test Drive',    d: 'Experience the car at your home' },
            { n: 3, t: 'Complete Purchase',  d: 'Simple documentation & instant delivery' },
            { n: 4, t: 'Drive Happy',        d: 'Enjoy 1-year warranty & 24x7 support' },
          ].map(({ n, t, d }) => (
            <div key={n} style={styles.stepCard}>
              <div style={styles.stepNumber}>{n}</div>
              <h3 style={styles.stepTitle}>{t}</h3>
              <p style={styles.stepDesc}>{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── CTA ── */}
      <section style={styles.ctaSection}>
        <h2 style={styles.ctaTitle}>Ready to Find Your Perfect Car?</h2>
        <p style={styles.ctaSubtitle}>
          Be among the first to experience a smarter, more transparent way to buy & sell cars with Caryugo
        </p>
        <button style={styles.ctaBtn} onClick={() => navigate('/buy-cars')}>
          Start Your Journey
        </button>
      </section>
    </div>
  );
};

/* ─────────────────────────── styles ─────────────────────────── */
const styles = {
  container: { width: '100%', overflowX: 'hidden' },

  /* hero */
  hero: {
    position: 'relative', height: '70vh',
    backgroundImage: `url(${heroImage})`,
    backgroundSize: 'cover', backgroundPosition: 'center',
    display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#FAFAFA',
  },
  heroOverlay: {
    width: '100%', height: '100%',
    background: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 20px',
  },
  heroContent:   { textAlign: 'center', maxWidth: '800px' },
  heroTitle:     { fontSize: '3.5rem', fontWeight: '700', marginBottom: '20px', lineHeight: '1.2', textShadow: '2px 2px 4px rgba(0,0,0,0.3)' },
  heroSubtitle:  { fontSize: '1.3rem', marginBottom: '40px', color: '#FFD60A', fontWeight: '500' },
  heroButtons:   { display: 'flex', gap: '20px', justifyContent: 'center', flexWrap: 'wrap' },
  primaryBtn:    { padding: '15px 40px', fontSize: '1.1rem', fontWeight: '600', backgroundColor: '#FFD60A', color: '#003566', border: 'none', borderRadius: '8px', cursor: 'pointer', boxShadow: '0 4px 15px rgba(255,214,10,0.3)' },
  secondaryBtn:  { padding: '15px 40px', fontSize: '1.1rem', fontWeight: '600', backgroundColor: 'transparent', color: '#FAFAFA', border: '2px solid #FAFAFA', borderRadius: '8px', cursor: 'pointer' },

  /* stats */
  statsSection:  { padding: '80px 20px', backgroundColor: '#FAFAFA' },
  statsGrid:     { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px,1fr))', gap: '30px', maxWidth: '1200px', margin: '0 auto' },
  statCard:      { textAlign: 'center', padding: '40px 20px', backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', cursor: 'pointer' },
  statNumber:    { fontSize: '2.5rem', fontWeight: '700', color: '#003566', margin: '20px 0 10px' },
  statLabel:     { fontSize: '1rem', color: '#666', fontWeight: '500' },

  /* story */
  storySection:  { padding: '80px 20px', backgroundColor: '#fff' },
  sectionContent:{ maxWidth: '1200px', margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '60px', alignItems: 'center' },
  storyText:     { paddingRight: '20px' },
  sectionTitle:  { fontSize: '2.5rem', fontWeight: '700', color: '#003566', marginBottom: '30px' },
  paragraph:     { fontSize: '1.1rem', lineHeight: '1.8', color: '#333', marginBottom: '20px' },
  storyImage:    { display: 'flex', justifyContent: 'center', alignItems: 'center' },
  imagePlaceholder: {
    width: '100%', height: '400px', borderRadius: '12px',
    backgroundImage: `url(${trustedCarImage})`,
    backgroundSize: 'cover', backgroundPosition: 'center',
    border: '3px solid #003566', overflow: 'hidden',
  },

  /* mission */
  missionSection:{ padding: '80px 20px', backgroundColor: '#003566' },
  missionGrid:   { maxWidth: '1200px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px,1fr))', gap: '40px' },
  missionCard:   { textAlign: 'center', padding: '50px 30px', backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: '12px', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.2)' },
  missionTitle:  { fontSize: '1.8rem', color: '#FFD60A', margin: '20px 0', fontWeight: '600' },
  missionText:   { fontSize: '1.1rem', color: '#FAFAFA', lineHeight: '1.8' },

  /* features */
  featuresSection: { padding: '80px 20px', backgroundColor: '#FAFAFA' },
  sectionSubtitle: { textAlign: 'center', fontSize: '1.2rem', color: '#666', marginBottom: '60px', maxWidth: '600px', margin: '0 auto 60px' },
  featuresGrid:  { maxWidth: '1200px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px,1fr))', gap: '30px' },
  featureCard:   { padding: '30px', backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', cursor: 'pointer', border: '2px solid transparent' },
  featureIcon:   { width: '60px', height: '60px', backgroundColor: '#FFD60A', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px' },
  featureTitle:  { fontSize: '1.2rem', fontWeight: '600', color: '#003566', marginBottom: '10px' },
  featureDesc:   { fontSize: '0.95rem', color: '#666', lineHeight: '1.6' },

  /* ── Our Journey (PhonePe style) ── */
  journeySection: {
    padding: '80px 0 0',
    backgroundColor: '#f0f4f8',
    overflow: 'hidden',
  },
  journeyHeading: {
    fontSize: '2.5rem',
    fontWeight: '700',
    color: '#003566',
    textAlign: 'center',
    marginBottom: '0',
    paddingBottom: '0',
  },

  /* stage — the big area with year + cards */
  journeyStage: {
    position: 'relative',
    height: '380px',
    maxWidth: '100%',
    margin: '30px 0 0',
    overflow: 'hidden',
  },

  roadSvg: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    pointerEvents: 'none',
  },

  /* central year */
  bigYear: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    fontSize: 'clamp(90px, 15vw, 160px)',
    fontWeight: '900',
    color: 'rgba(0,53,102,0.10)',
    letterSpacing: '-4px',
    userSelect: 'none',
    lineHeight: '1',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    pointerEvents: 'none',
    fontFamily: 'Georgia, serif',
  },
  upcomingBadge: {
    marginTop: '6px',
    fontSize: '0.9rem',
    fontWeight: '700',
    letterSpacing: '3px',
    color: '#FFD60A',
    backgroundColor: '#003566',
    padding: '4px 14px',
    borderRadius: '20px',
    textTransform: 'uppercase',
    opacity: 1,
  },

  /* milestone card */
  milestoneCard: {
    position: 'absolute',
    width: 'clamp(160px, 20vw, 220px)',
    padding: '14px 16px',
    borderRadius: '12px',
    boxShadow: '0 6px 24px rgba(0,0,0,0.18)',
    zIndex: 10,
  },
  connector: {
    position: 'absolute',
    width: '1px',
    height: '36px',
    borderLeft: '2px dashed',
    opacity: 0.6,
  },
  cardEmoji: { fontSize: '1.5rem', display: 'block', marginBottom: '6px' },
  cardTitle: { fontSize: '0.95rem', fontWeight: '700', marginBottom: '4px', lineHeight: '1.3' },
  cardDesc:  { fontSize: '0.78rem', lineHeight: '1.45', opacity: 0.88 },

  /* cube accents */
  cubeLeft: {
    position: 'absolute',
    width: '60px',
    bottom: '18%',
    left: '26%',
    opacity: 0.7,
    pointerEvents: 'none',
  },
  cubeRight: {
    position: 'absolute',
    width: '44px',
    top: '18%',
    right: '24%',
    opacity: 0.6,
    pointerEvents: 'none',
  },

  /* year nav strip */
  yearNav: {
    backgroundColor: '#fff',
    padding: '28px 40px 32px',
    marginTop: '0',
    boxShadow: '0 -2px 20px rgba(0,0,0,0.06)',
  },
  yearTrack: {
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    gap: '80px',
    maxWidth: '400px',
    margin: '0 auto',
    alignItems: 'flex-start',
    paddingTop: '10px',
  },
  yearTrackLine: {
    position: 'absolute',
    top: '18px',
    left: '10%',
    right: '10%',
    height: '2px',
    backgroundColor: '#e0e0e0',
    zIndex: 0,
  },
  yearStop: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '10px',
    zIndex: 1,
    cursor: 'pointer',
  },
  yearDot: {
    width: '18px',
    height: '18px',
    borderRadius: '50%',
    backgroundColor: '#ccc',
  },
  yearLabel: {
    fontSize: '1rem',
    color: '#999',
    fontWeight: '400',
    whiteSpace: 'nowrap',
  },
  visionTag: { color: '#FFD60A', fontSize: '0.8rem' },

  /* how it works */
  howItWorksSection: { padding: '80px 20px', backgroundColor: '#FAFAFA' },
  stepsGrid: { maxWidth: '1200px', margin: '60px auto 0', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px,1fr))', gap: '30px' },
  stepCard:  { textAlign: 'center', padding: '40px 20px', backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', cursor: 'pointer' },
  stepNumber:{ width: '60px', height: '60px', backgroundColor: '#003566', color: '#FFD60A', fontSize: '2rem', fontWeight: '700', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' },
  stepTitle: { fontSize: '1.3rem', fontWeight: '600', color: '#003566', marginBottom: '10px' },
  stepDesc:  { fontSize: '1rem', color: '#666', lineHeight: '1.6' },

  /* cta */
  ctaSection:  { padding: '100px 20px', background: '#F5EBDD', textAlign: 'center' },
  ctaTitle:    { fontSize: '2.5rem', fontWeight: '700', marginBottom: '20px', color: '#003566' },
  ctaSubtitle: { fontSize: '1.2rem', marginBottom: '40px', color: '#555' },
  ctaBtn:      { padding: '18px 50px', fontSize: '1.2rem', fontWeight: '600', backgroundColor: '#FFD60A', color: '#003566', border: 'none', borderRadius: '8px', cursor: 'pointer', boxShadow: '0 6px 20px rgba(255,214,10,0.4)' },
};

export default AboutUs;